import React from 'react';
import {
  mentionsSettings,
  sourceConnectionConfig,
  sourceLabel,
  type ConnectableSource,
} from '../../domain/sourceConnection';
import { CardSkeleton, EmptyState, ErrorState } from '../../ui/common';
import { openSettingsSection } from '../settings/settingsDeepLink';

export interface SourceFeedStateProps {
  source: ConnectableSource;
  error: string | null;
  isLoading: boolean;
  isRetrying: boolean;
  onRetry: () => void;
  totalCount: number;
  visibleCount: number;
  onShowAll: () => void;
  listGap?: number;
  children: React.ReactNode;
}

/**
 * Shared error / loading / empty chrome for a *connected* provider feed.
 * Connection gating (not configured → `SourceConnectionPanel`) happens one
 * level up via `useSourceConnectionGate`; this component only deals with
 * load failures, skeletons and empty lists.
 */
export const SourceFeedState: React.FC<SourceFeedStateProps> = ({
  source,
  error,
  isLoading,
  isRetrying,
  onRetry,
  totalCount,
  visibleCount,
  onShowAll,
  listGap = 14,
  children,
}) => {
  const config = sourceConnectionConfig(source);
  const settingsAction = mentionsSettings(error)
    ? {
        actionLabel: `Abrir Configuración → ${sourceLabel(source)}`,
        onAction: () => openSettingsSection(config.settingsSection),
      }
    : {};

  const renderEmpty = () => {
    if (visibleCount > 0 || error) return null;
    if (totalCount > 0) {
      return (
        <EmptyState
          message={config.filterEmptyMessage}
          actionLabel="Ver todos"
          onAction={onShowAll}
        />
      );
    }
    return <EmptyState message={config.emptyMessage} />;
  };

  return (
    <>
      {error ? (
        <ErrorState
          title={`Error en ${sourceLabel(source)}`}
          message={error}
          onRetry={onRetry}
          isRetrying={isRetrying}
          {...settingsAction}
        />
      ) : null}
      {isLoading ? (
        <CardSkeleton count={3} variant={source} height={config.skeletonHeight} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: listGap }}>
          {children}
          {renderEmpty()}
        </div>
      )}
    </>
  );
};
